import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DoorOpen, X } from 'lucide-react';

export default function AddRoomModal({ isOpen, onClose, onSave, loading, floor, defaultRent, roomCount }) {
  const [formData, setFormData] = useState({
    roomNumber: '',
    roomType: 'Shared',
    numBeds: 2,
    rentPerBed: defaultRent || 5000
  });

  useEffect(() => {
    if (isOpen) {
      setFormData({
        roomNumber: `${roomCount + 1}`,
        roomType: 'Shared',
        numBeds: 2,
        rentPerBed: defaultRent || 5000
      });
    }
  }, [isOpen, defaultRent, roomCount]);

  if (!isOpen || !floor) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      floorId: floor.id,
      roomNumber: formData.roomNumber.trim(),
      roomType: formData.roomType,
      numBeds: Number(formData.numBeds),
      rentPerBed: Number(formData.rentPerBed)
    });
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full" data-testid="add-room-modal">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <DoorOpen className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900 font-heading">Add Room</h2>
              <p className="text-sm text-slate-500">{floor.floorName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            data-testid="close-add-room-modal"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Room Number *</Label>
            <Input
              value={formData.roomNumber}
              onChange={(e) => setFormData({ ...formData, roomNumber: e.target.value })}
              className="h-12 rounded-lg"
              required
              data-testid="add-room-number"
            />
          </div>

          <div>
            <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Room Type *</Label>
            <select
              value={formData.roomType}
              onChange={(e) => setFormData({ ...formData, roomType: e.target.value })}
              className="h-12 w-full px-4 rounded-lg border border-slate-300 bg-white focus:ring-2 focus:ring-primary/20"
              data-testid="add-room-type"
            >
              <option>Shared</option>
              <option>Private</option>
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Beds *</Label>
              <Input
                type="number"
                min="1"
                max="10"
                value={formData.numBeds}
                onChange={(e) => setFormData({ ...formData, numBeds: Number(e.target.value) })}
                className="h-12 rounded-lg"
                required
                data-testid="add-room-beds"
              />
            </div>
            <div>
              <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Rent per Bed (₹) *</Label>
              <Input
                type="number"
                min="1"
                value={formData.rentPerBed}
                onChange={(e) => setFormData({ ...formData, rentPerBed: Number(e.target.value) })}
                className="h-12 rounded-lg"
                required
                data-testid="add-room-rent"
              />
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              className="flex-1 h-12 rounded-xl"
              disabled={loading}
              data-testid="cancel-add-room"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 h-12 rounded-xl bg-primary"
              disabled={loading}
              data-testid="save-add-room"
            >
              {loading ? 'Adding...' : 'Add Room'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
